import { jwtDecode } from 'jwt-decode';
import { apiClient } from './api';
import type { User, LoginCredentials, ApiResponse, ChangePasswordRequest, ChangePasswordResponse, RegisterRequest, RegisterResponse, PasswordResetResponse, PasswordResetConfirmRequest } from '../types';

interface JwtPayload {
  sub: string;
  email: string;
  role: string;
  exp: number;
  iat: number;
}

interface LoginResponse {
  token: string;
  user: User;
}

export interface TwoFactorRequiredResponse {
  requires_2fa: true;
  temp_token: string;
}

export type LoginResult =
  | { success: true; data: LoginResponse; requires_2fa: false }
  | { success: true; requires_2fa: true; temp_token: string }
  | { success: false; error: string };

export interface TwoFactorSetupData {
  secret: string;
  qr_uri: string;
  qr_image_b64: string;
}

export class AuthService {
  private static readonly TOKEN_KEY = 'auth_token';
  private static readonly USER_KEY = 'auth_user';

  /**
   * Step 1 of login. Returns one of:
   *  - { success: true, requires_2fa: false, data } — fully logged in
   *  - { success: true, requires_2fa: true, temp_token } — TOTP code required
   *  - { success: false, error } — bad credentials
   */
  static async login(credentials: LoginCredentials, signal?: AbortSignal): Promise<LoginResult> {
    const response = await apiClient.post<LoginResponse | TwoFactorRequiredResponse>(
      '/api/admin/auth/login',
      credentials,
      signal ? { signal } : undefined,
    );

    if (!response.success) {
      return {
        success: false,
        error: response.error?.message ?? 'Invalid email or password. Please try again.',
      };
    }

    const data = response.data!;

    // 2FA required — backend returned temp_token instead of a real JWT
    if ('requires_2fa' in data && data.requires_2fa) {
      return { success: true, requires_2fa: true, temp_token: data.temp_token };
    }

    // Full login
    const full = data as LoginResponse;
    this.setToken(full.token);
    this.setUser(full.user);
    return { success: true, requires_2fa: false, data: full };
  }

  /**
   * Step 2 of login — submit the 6-digit TOTP code.
   */
  static async verifyTwoFactor(
    temp_token: string,
    code: string,
  ): Promise<ApiResponse<LoginResponse>> {
    const response = await apiClient.post<LoginResponse>('/api/admin/auth/verify-2fa', {
      temp_token,
      code,
    });
    if (response.success && response.data) {
      this.setToken(response.data.token);
      this.setUser(response.data.user);
    }
    return response;
  }

  // ── Social sign-in (Google / LinkedIn) ─────────────────────────────────────

  /** Sign in / sign up with a Google Identity Services ID token. */
  static async oauthGoogle(idToken: string): Promise<LoginResult> {
    const response = await apiClient.post<LoginResponse | TwoFactorRequiredResponse>(
      '/api/admin/auth/oauth/google',
      { id_token: idToken },
    );
    return this.completeSocialLogin(response);
  }

  /** Sign in / sign up with a LinkedIn authorization code (state already validated). */
  static async oauthLinkedIn(code: string): Promise<LoginResult> {
    const response = await apiClient.post<LoginResponse | TwoFactorRequiredResponse>(
      '/api/admin/auth/oauth/linkedin',
      { code },
    );
    return this.completeSocialLogin(response);
  }

  private static completeSocialLogin(
    response: ApiResponse<LoginResponse | TwoFactorRequiredResponse>,
  ): LoginResult {
    if (!response.success) {
      return {
        success: false,
        error: response.error?.message ?? 'Sign-in failed. Please try again.',
      };
    }

    const data = response.data!;

    // Backend returned the 2FA challenge instead of a full JWT
    if ('requires_2fa' in data && data.requires_2fa) {
      return { success: true, requires_2fa: true, temp_token: data.temp_token };
    }

    const full = data as LoginResponse;
    this.setToken(full.token);
    this.setUser(full.user);
    return { success: true, requires_2fa: false, data: full };
  }

  static async logout(): Promise<void> {
    try {
      await apiClient.post('/api/admin/auth/logout');
    } catch (error) {
      console.warn('Logout API call failed:', error);
    } finally {
      this.clearAuth();
    }
  }

  static async getCurrentUser(): Promise<ApiResponse<User>> {
    return apiClient.get<User>('/api/admin/auth/profile');
  }

  static async changePassword(passwordData: ChangePasswordRequest): Promise<ApiResponse<ChangePasswordResponse>> {
    return apiClient.post<ChangePasswordResponse>('/api/admin/auth/change-password', passwordData);
  }

  static async requestPasswordReset(email: string): Promise<ApiResponse<PasswordResetResponse>> {
    return apiClient.post<PasswordResetResponse>('/api/admin/auth/request-password-reset', { email });
  }

  static async resetPassword(data: PasswordResetConfirmRequest): Promise<ApiResponse<PasswordResetResponse>> {
    return apiClient.post<PasswordResetResponse>('/api/admin/auth/reset-password', data);
  }

  static async register(data: RegisterRequest): Promise<ApiResponse<RegisterResponse>> {
    return apiClient.post<RegisterResponse>('/api/admin/auth/register', data);
  }

  // ── 2FA management ──────────────────────────────────────────────────────

  static async get2FAStatus(): Promise<ApiResponse<{ enabled: boolean }>> {
    return apiClient.get('/api/admin/auth/2fa/status');
  }

  static async setup2FA(): Promise<ApiResponse<TwoFactorSetupData>> {
    return apiClient.post('/api/admin/auth/2fa/setup', {});
  }

  static async confirm2FA(code: string): Promise<ApiResponse<{ success: boolean; message: string }>> {
    return apiClient.post('/api/admin/auth/2fa/confirm', { code });
  }

  static async disable2FA(password: string): Promise<ApiResponse<{ success: boolean; message: string }>> {
    return apiClient.post('/api/admin/auth/2fa/disable', { password });
  }

  // ── Token helpers ────────────────────────────────────────────────────────

  static getToken(): string | null {
    const token_localstorage = localStorage.getItem(this.TOKEN_KEY);
    const token = sessionStorage.getItem(this.TOKEN_KEY);
    if (token_localstorage === token) return token;
    if (!token) return token_localstorage;
    return null;
  }

  static setToken(token: string): void {
    localStorage.setItem(this.TOKEN_KEY, token);
    sessionStorage.setItem(this.TOKEN_KEY, token);
  }

  static getUser(): User | null {
    const user_localstorage = localStorage.getItem(this.USER_KEY);
    const userStr = sessionStorage.getItem(this.USER_KEY) || user_localstorage;

    if (user_localstorage !== userStr) return null;
    
    if (!userStr) return null;
    try {
      return JSON.parse(userStr);
    } catch {
      return null;
    }
  }

  static setUser(user: User): void {
    sessionStorage.setItem(this.USER_KEY, JSON.stringify(user));
    localStorage.setItem(this.USER_KEY, JSON.stringify(user));
  }

  static clearAuth(): void {
    sessionStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.TOKEN_KEY);
    sessionStorage.removeItem(this.USER_KEY);
    localStorage.removeItem(this.USER_KEY);
  }

  static isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) return false;
    try {
      const decoded = jwtDecode<JwtPayload>(token);
      return decoded.exp > Date.now() / 1000;
    } catch {
      return false;
    }
  }

  static hasRole(requiredRole: string): boolean {
    const user = this.getUser();
    if (!user) return false;
    if (requiredRole === 'super_admin') return user.role === 'super_admin';
    if (requiredRole === 'marketing_agent') return user.role === 'marketing_agent' || user.role === 'super_admin';
    return false;
  }

  static getUserRole(): string | null {
    return this.getUser()?.role ?? null;
  }
}
